import React, { useState, useMemo, useEffect } from 'react';
import { FiX, FiSearch, FiFileText } from 'react-icons/fi';
import '../styles/codiii-kit.css';

/**
 * Centered modal for adding additional documents to a compliance scan.
 * Renders in the center of the viewport with a backdrop.
 */
const AddAdditionalDocumentsModal = ({
  isOpen,
  onClose,
  availableDocs = [],
  onConfirm,
  getId = (doc) => doc.docId ?? doc.id ?? doc.s3Key ?? doc.key ?? `${doc.bucket || ''}/${doc.title || doc.name || ''}`,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const getStableId = (d) => getId(d);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableDocs;
    return availableDocs.filter((d) => {
      const hay = `${d.title || d.name || ''} ${d.type || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [availableDocs, search]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const toAdd = filteredDocs.filter((d) => selectedIds.has(getStableId(d)));
    if (toAdd.length && onConfirm) onConfirm(toAdd);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="add-docs-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-docs-modal-title"
    >
      <div className="add-docs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-docs-modal__header">
          <h2 id="add-docs-modal-title" className="add-docs-modal__title">
            Add Additional Documents
          </h2>
          <button
            type="button"
            className="add-docs-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={22} />
          </button>
        </div>

        <div className="add-docs-modal__body">
          <div className="add-docs-modal__search-wrap">
            <FiSearch size={18} aria-hidden />
            <input
              type="text"
              placeholder="Search project documents…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="add-docs-modal__search-input"
            />
          </div>

          <div className="add-docs-modal__list">
            {filteredDocs.length === 0 ? (
              <div className="add-docs-modal__empty">
                {search ? 'No matches.' : 'All project documents are already included.'}
              </div>
            ) : (
              filteredDocs.map((d) => {
                const id = getStableId(d);
                const checked = selectedIds.has(id);
                return (
                  <label key={id} className="add-docs-modal__item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(id)}
                      className="add-docs-modal__item-checkbox"
                    />
                    <span className="add-docs-modal__item-icon">
                      <FiFileText size={16} />
                    </span>
                    <div className="add-docs-modal__item-text">
                      <span className="add-docs-modal__item-title">
                        {d.title || d.name || 'Untitled Document'}
                      </span>
                      {(d.type || d.pages) && (
                        <span className="add-docs-modal__item-meta">
                          {[d.type, d.pages ? `${d.pages} pp` : ''].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="add-docs-modal__footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="filter-apply"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
          >
            Add {selectedIds.size || ''} {selectedIds.size === 1 ? 'Document' : 'Documents'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAdditionalDocumentsModal;
